import React, { useState, useEffect } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Building2,
  Users,
  FileText,
  Check,
  X,
  ImageOff,
  Loader2,
  ArrowRightLeft,
  Star,
  Calculator,
  Utensils
} from 'lucide-react';
import { Button } from '@/components/ui/Button/Button';
import { Badge } from '@/components/ui/Badge/Badge';
import { Textarea } from '@/components/ui/Textarea/Textarea';
import { SimpleTooltip } from '@/components/ui/Tooltip';
import { cn } from '@/lib/utils';
import { useVenuePhotos } from '@/hooks/useVenuePhotos';
import { StarRating } from '@/components/ui/StarRating';
import { formatCurrency } from '@/lib/currencyFormat';

export interface VenueSelectionCardVenue {
  id: string;
  property_name: string;
  city: string;
  country: string | null;
  number_of_rooms: number | null;
  maximum_capacity: number | null;
  star_rating: number | null;
  place_id: string | null;
  website: string | null;
  address: string | null;
}

export interface VenueSelectionCardShortlistItem {
  id: string;
  venue: VenueSelectionCardVenue;
  cvent_proposal_status: string | null;
  cvent_lowest_room_rate: number | null;
  cvent_total_cost: number | null;
  cvent_currency_code: string | null;
  cvent_proposal_pdf_attachment_id: string | null;
  approval_status: 'approved' | 'denied' | null;
  is_recommended: boolean;
  is_buyout: boolean;
  is_all_inclusive: boolean;
  comparison_budget_note: string | null;
}

export interface VenueSelectionCardProps {
  item: VenueSelectionCardShortlistItem;
  onApprove: (shortlistId: string) => void;
  onDeny: (shortlistId: string) => void;
  /** Permanent hotel notes box (shared with Research and RFP steps). */
  notesSection?: React.ReactNode;
  onToggleRecommended?: (shortlistId: string, isRecommended: boolean) => void;
  onToggleBuyout?: (shortlistId: string, isBuyout: boolean) => void;
  onToggleAllInclusive?: (shortlistId: string, isAllInclusive: boolean) => void;
  onComparisonBudgetNoteChange?: (shortlistId: string, note: string | null) => void | Promise<void>;
  onViewProposal?: (shortlistId: string) => void;
  onViewDetails?: (venueId: string) => void;
  onViewBreakdown?: (shortlistId: string) => void;
  onMoveToDestination?: (shortlistId: string) => void;
  isUpdating?: boolean;
  variant?: 'light' | 'dark';
  className?: string;
}

/**
 * VenueSelectionCard - A reusable card component for venue selection
 *
 * Features:
 * - Image carousel with Google Places photos (swipeable on mobile)
 * - Venue information (name, rooms, capacity, stars, costs)
 * - Proposal PDF icon (clickable if available)
 * - Permanent notes box (notesSection) and one-click Approve/Deny
 */
export function VenueSelectionCard({
  item,
  onApprove,
  onDeny,
  notesSection,
  onToggleRecommended,
  onToggleBuyout,
  onToggleAllInclusive,
  onComparisonBudgetNoteChange,
  onViewProposal,
  onViewDetails,
  onViewBreakdown,
  onMoveToDestination,
  isUpdating = false,
  variant = 'light',
  className,
}: VenueSelectionCardProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [comparisonBudgetNote, setComparisonBudgetNote] = useState(item.comparison_budget_note || '');
  const [pendingAction, setPendingAction] = useState<'approve' | 'deny' | null>(null);

  const { photos, isLoading: isLoadingPhotos } = useVenuePhotos(item.venue.id, true);

  // Reset state when item changes
  useEffect(() => {
    setComparisonBudgetNote(item.comparison_budget_note || '');
    setPendingAction(null);
  }, [item.id, item.comparison_budget_note]);

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : photos.length - 1));
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => (prev < photos.length - 1 ? prev + 1 : 0));
  };

  // Notes now live in the permanent notes box, so a decision is one click.
  const handleApproveClick = () => {
    setPendingAction('approve');
    onApprove(item.id);
  };

  const handleDenyClick = () => {
    setPendingAction('deny');
    onDeny(item.id);
  };

  const handleComparisonBudgetNoteBlur = () => {
    if (!onComparisonBudgetNoteChange) return;

    const normalizedNote = comparisonBudgetNote.trim() || null;
    const currentNote = item.comparison_budget_note?.trim() || null;
    if (normalizedNote === currentNote) return;

    onComparisonBudgetNoteChange(item.id, normalizedNote);
  };


  const isDark = variant === 'dark';

  return (
    <div
      className={cn(
        'flex flex-col md:flex-row rounded-xl overflow-hidden border transition-shadow hover:shadow-lg',
        isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200',
        item.approval_status === 'approved' && 'ring-2 ring-success-500',
        item.approval_status === 'denied' && 'ring-2 ring-error-500 opacity-75',
        className
      )}
    >
      {/* Image Carousel Section */}
      <div className="relative w-full md:w-64 h-48 md:h-auto md:aspect-square flex-shrink-0 bg-gray-100">
        {isLoadingPhotos ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
          </div>
        ) : photos.length > 0 ? (
          <>
            <img
              src={photos[currentImageIndex]?.url}
              alt={`${item.venue.property_name} - Photo ${currentImageIndex + 1}`}
              className="w-full h-full object-cover"
            />

            {/* Navigation arrows (desktop only) */}
            {photos.length > 1 && (
              <>
                <button
                  onClick={handlePrevImage}
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors hidden md:flex"
                  aria-label="Previous image"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={handleNextImage}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors hidden md:flex"
                  aria-label="Next image"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </>
            )}

            {/* Image counter */}
            {photos.length > 1 && (
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 px-2 py-1 rounded-full bg-black/50 text-white text-xs">
                {currentImageIndex + 1} / {photos.length}
              </div>
            )}
          </>
        ) : (
          <div className={cn(
            'absolute inset-0 flex flex-col items-center justify-center',
            isDark ? 'bg-gray-800' : 'bg-gray-100'
          )}>
            <ImageOff className={cn('w-12 h-12 mb-2', isDark ? 'text-gray-600' : 'text-gray-300')} />
            <span className={cn('text-sm', isDark ? 'text-gray-500' : 'text-gray-400')}>
              No images available
            </span>
          </div>
        )}

        {/* Approval status, Recommended, and Buyout badges */}
        {(item.approval_status || item.is_recommended || item.is_buyout) && (
          <div className="absolute top-2 right-2 flex flex-col gap-1 items-end">
            {item.approval_status && (
              <Badge
                variant={item.approval_status === 'approved' ? 'success' : 'error'}
                className="capitalize"
              >
                {item.approval_status}
              </Badge>
            )}
            {item.is_recommended && (
              <Badge
                variant="primary"
                className="flex items-center gap-1"
              >
                <Star className="w-3 h-3 fill-current" />
                Recommended
              </Badge>
            )}
            {item.is_buyout && (
              <Badge
                variant="warning"
                className="flex items-center gap-1"
              >
                <Check className="w-3 h-3" />
                Buyout
              </Badge>
            )}
          </div>
        )}
      </div>

      {/* Content Section */}
      <div className="flex-1 p-4 flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className={cn(
                'font-semibold text-lg leading-tight',
                isDark ? 'text-white' : 'text-gray-900'
              )}>
                {item.venue.property_name}
              </h3>
              {item.is_all_inclusive && (
                <Badge
                  variant="secondary"
                  className={cn(
                    'inline-flex items-center gap-1 rounded-full border bg-white px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide shadow-sm',
                    isDark
                      ? 'border-white/20 text-gray-900'
                      : 'border-gray-200 text-gray-900'
                  )}
                >
                  <Utensils className="w-3 h-3" />
                  All-Inclusive
                </Badge>
              )}
            </div>
            <p className={cn('text-sm', isDark ? 'text-gray-400' : 'text-muted-foreground')}>
              {item.venue.city}{item.venue.country ? `, ${item.venue.country}` : ''}
            </p>
          </div>

          {/* Star rating - using emoji for consistency with Research step */}
          {item.venue.star_rating && (
            <div className="flex-shrink-0">
              <StarRating rating={item.venue.star_rating} size="sm" showEmptyStars={true} />
            </div>
          )}
        </div>

        {/* Venue Stats */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 mb-3 text-sm">
          <div className="flex items-center gap-2">
            <Building2 className={cn('w-4 h-4', isDark ? 'text-gray-500' : 'text-gray-400')} />
            <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>
              {item.venue.number_of_rooms || '-'} rooms
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Users className={cn('w-4 h-4', isDark ? 'text-gray-500' : 'text-gray-400')} />
            <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>
              {item.venue.maximum_capacity || '-'} max capacity
            </span>
          </div>
        </div>

        {/* Cost Information */}
        <div className="flex flex-wrap gap-4 mb-4 text-sm">
          <div>
            <span className={cn('block', isDark ? 'text-gray-500' : 'text-muted-foreground')}>
              Lowest Room Rate
            </span>
            <span className={cn('font-semibold', isDark ? 'text-white' : 'text-gray-900')}>
              {formatCurrency(item.cvent_lowest_room_rate, item.cvent_currency_code ?? 'USD')}
            </span>
          </div>
        </div>

        {/* Hotel notes — same note as the Research and RFP steps */}
        {notesSection && <div className="mb-3">{notesSection}</div>}

        {/* Comparison Budget Note - shown only on proposal comparison budget block */}
        {onComparisonBudgetNoteChange && (
          <div className={cn(
            'mb-3 p-3 rounded-lg border',
            isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
          )}>
            <Textarea
              label="Additional Details"
              hint="Shown only under Destination in the proposal comparison budget block."
              placeholder="Details to show in the proposal comparison budget..."
              value={comparisonBudgetNote}
              onChange={(e) => setComparisonBudgetNote(e.target.value)}
              onBlur={handleComparisonBudgetNoteBlur}
              rows={2}
              className={cn(
                'text-sm min-h-[72px]',
                isDark ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white'
              )}
            />
          </div>
        )}

        {/* Actions */}
        <div className="mt-auto flex flex-col gap-3">
          {/* First Row: Recommended, Buyout, All-Inclusive, View Details, Proposal, Move */}
          <div className="flex items-center gap-2 flex-wrap">
            {/* Recommended Toggle */}
            {onToggleRecommended && (
              <SimpleTooltip
                content={item.is_recommended ? 'Remove recommendation' : 'Mark as recommended'}
                side="top"
              >
                <Button
                  variant={item.is_recommended ? 'primary' : 'outline'}
                  size="sm"
                  onClick={() => onToggleRecommended(item.id, !item.is_recommended)}
                  disabled={isUpdating}
                  className={cn(
                    "flex-shrink-0",
                    item.is_recommended && "bg-primary-600 hover:bg-primary-700"
                  )}
                  leftIcon={<Star className={cn("w-4 h-4", item.is_recommended && "fill-current")} />}
                >
                  {item.is_recommended ? 'Recommended' : 'Recommend'}
                </Button>
              </SimpleTooltip>
            )}

            {/* Buyout Toggle */}
            {onToggleBuyout && (
              <SimpleTooltip
                content={item.is_buyout ? 'Remove buyout marker' : 'Mark as buyout'}
                side="top"
              >
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onToggleBuyout(item.id, !item.is_buyout)}
                  disabled={isUpdating}
                  aria-pressed={item.is_buyout}
                  className={cn(
                    "flex-shrink-0 border-warning-300 text-warning-700 hover:bg-warning-50 hover:text-warning-800",
                    item.is_buyout && "border-warning-500 bg-warning-100 text-warning-900 hover:bg-warning-200 hover:text-warning-900"
                  )}
                  leftIcon={<Check className="w-4 h-4" />}
                >
                  {item.is_buyout ? 'Buyout selected' : 'Buyout'}
                </Button>
              </SimpleTooltip>
            )}

            {/* All-Inclusive Toggle */}
            {onToggleAllInclusive && (
              <SimpleTooltip
                content={item.is_all_inclusive ? 'Remove all-inclusive marker' : 'Mark as all-inclusive'}
                side="top"
              >
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onToggleAllInclusive(item.id, !item.is_all_inclusive)}
                  disabled={isUpdating}
                  aria-pressed={item.is_all_inclusive}
                  className={cn(
                    "flex-shrink-0 border-secondary-300 text-secondary-700 hover:bg-secondary-50 hover:text-secondary-800",
                    item.is_all_inclusive && "border-secondary-500 bg-secondary-100 text-secondary-900 hover:bg-secondary-200 hover:text-secondary-900"
                  )}
                  leftIcon={<Utensils className="w-4 h-4" />}
                >
                  {item.is_all_inclusive ? 'All-Inclusive selected' : 'All-Inclusive'}
                </Button>
              </SimpleTooltip>
            )}

            {/* View Details Button */}
            {onViewDetails && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onViewDetails(item.venue.id)}
                className="flex-shrink-0 text-muted-foreground hover:text-foreground"
              >
                View Details
              </Button>
            )}

            {/* View Breakdown Button - Always show if prop is provided */}
            {onViewBreakdown && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onViewBreakdown(item.id)}
                leftIcon={<Calculator className="w-4 h-4" />}
                className="flex-shrink-0 text-muted-foreground hover:text-foreground"
              >
                View Breakdown
              </Button>
            )}

            {/* Proposal PDF Button - Less prominent */}
            {item.cvent_proposal_pdf_attachment_id ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onViewProposal?.(item.id)}
                leftIcon={<FileText className="w-4 h-4" />}
                className="flex-shrink-0 text-muted-foreground hover:text-foreground"
              >
                View Proposal
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                disabled
                leftIcon={<FileText className="w-4 h-4 text-gray-300" />}
                className="flex-shrink-0 cursor-not-allowed opacity-50"
                title="Proposal PDF not available"
              >
                No Proposal
              </Button>
            )}

            {/* Move to Different Destination Button */}
            {onMoveToDestination && (
              <SimpleTooltip content="Move to different destination" side="top">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onMoveToDestination(item.id)}
                  disabled={isUpdating}
                  className="flex-shrink-0 text-muted-foreground hover:text-primary-600 hover:bg-primary-50"
                >
                  <ArrowRightLeft className="w-4 h-4" />
                </Button>
              </SimpleTooltip>
            )}
          </div>

          {/* Second Row: Approve/Deny Actions - Left aligned */}
          <div className="flex items-center gap-2">
            {/* Approval Actions - Both always visible to allow changing decisions */}
            <Button
              variant={item.approval_status === 'approved' ? 'success' : 'outline'}
              size="sm"
              onClick={handleApproveClick}
              disabled={isUpdating}
              leftIcon={isUpdating && pendingAction === 'approve' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            >
              {item.approval_status === 'approved' ? 'Approved' : 'Approve'}
            </Button>

            <Button
              variant={item.approval_status === 'denied' ? 'error' : 'outline'}
              size="sm"
              onClick={handleDenyClick}
              disabled={isUpdating}
              leftIcon={isUpdating && pendingAction === 'deny' ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
            >
              {item.approval_status === 'denied' ? 'Denied' : 'Deny'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VenueSelectionCard;
